// Keycloak OIDC config per ADR-007 — `customers` realm, public PKCE client.
//
// Phase 0: passwordless phone-OTP authenticator (ADR-007 Action Item #4 —
// Java SPI plug-in) doesn't exist yet; the realm falls back to Keycloak's
// built-in password authenticator. The OIDC dance is identical either way
// from the SPA side — the realm's flow decides what the user sees.
//
// All values overridable via VITE_KEYCLOAK_* env vars so per-environment
// builds (staging, prod, on-prem) point at the right authority without
// rebuilding the bundle.

import type { AuthProviderProps } from "react-oidc-context";
import { WebStorageStateStore } from "oidc-client-ts";

import { resolveCustomerKeycloakAuthority } from "@/config/customer-deployment";
import { isDevAuthActive } from "@/auth/dev-auth-flag";

import { guardTerminatedUserStore } from "./session-termination";

// Exported so the DEV-ONLY dev-auth bootstrap (src/auth/dev-auth.ts) builds
// its UserManager from the SAME authority/client_id/redirect_uri — that's
// what makes storeUser() write under the EXACT storage key react-oidc-context
// reads (oidc.user:<authority>:<client_id>). One source, both consumers.
// Resolve before AuthProvider receives an authority. Production therefore
// fails during bundle initialization rather than redirecting a browser to an
// omitted/local endpoint.
export const oidcAuthority = resolveCustomerKeycloakAuthority({
  configuredUrl: import.meta.env["VITE_KEYCLOAK_AUTHORITY"],
  mode: import.meta.env.PROD ? "production" : "development",
});
export const oidcClientId = import.meta.env["VITE_KEYCLOAK_CLIENT_ID"] ?? "customer-web";
export const oidcRedirectUri =
  import.meta.env["VITE_OIDC_REDIRECT_URI"] ??
  (typeof window !== "undefined" ? `${window.location.origin}/callback` : "");
const postLogoutRedirectUri =
  import.meta.env["VITE_OIDC_POST_LOGOUT_URI"] ??
  (typeof window !== "undefined" ? window.location.origin : "");

/** sessionStorage-backed OIDC user store (ADR-007 — tokens never in localStorage). */
export const oidcUserStore =
  typeof window !== "undefined"
    ? guardTerminatedUserStore(new WebStorageStateStore({ store: window.sessionStorage }))
    : undefined;

export const oidcConfig: AuthProviderProps = {
  authority: oidcAuthority,
  client_id: oidcClientId,
  redirect_uri: oidcRedirectUri,
  post_logout_redirect_uri: postLogoutRedirectUri,
  response_type: "code",
  scope: "openid profile email",
  loadUserInfo: true,
  // DEV-ONLY: the dev-auth bootstrap pre-seeds an 8h HS256 token directly into
  // the OIDC store, so there's no Keycloak to silently renew against — leaving
  // renewal on would fire a request to the (down) authority and surface as
  // "Sign-in failed". Production keeps automatic silent renew on (unchanged).
  automaticSilentRenew: !isDevAuthActive(),
  // Keep tokens out of localStorage (XSS exposure); sessionStorage scopes
  // them to the tab and clears on close.
  userStore: oidcUserStore,
  onSigninCallback: (): void => {
    // Strip the OIDC params from the URL after the redirect lands so the
    // user never sees ?code=... in their address bar.
    if (typeof window !== "undefined") {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  },
};
